import logo from "../../assets/icons/brand/logo.svg";
import {Link} from "react-router-dom";
import {ReactElement, useContext, useEffect, useState} from "react";
import loginGraphic from "./loginGraphic";
import {Field, Input, Label} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Col, Grid, Row} from "@zendeskgarden/react-grid";
import * as css from "./styles/SignIn.module.scss";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {ViziblyTheme} from "../../styles/zendesk-garden/ViziblyZDGTheme";
import {ClientAuthenticationType, useGetAuthConfigLazyQuery} from "../../__generated__/generated_types";
import {redirectBrowser} from "../../global/browser_functions";
import {AuthContext} from "../../contexts/AuthContext";
import {isEmailValid} from "../../utils/exports";
import {useRoutingUtils} from "../../routes/RoutingUtils";
import {SignInChangePassword} from "../../components/auth/SignInChangePassword";
import {ReactComponent as Eye} from "@zendeskgarden/svg-icons/src/16/eye-stroke.svg";
import {Visibility, VisibilityOff} from "@material-ui/icons";

export function SignIn(): ReactElement {

    const [usePassword, setUsePassword] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [checking, setChecking] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);

    const [getAuthConfig, {data: authConfig, loading: authConfigLoading}] = useGetAuthConfigLazyQuery({
        fetchPolicy: "no-cache"
    });

    const authContext = useContext(AuthContext);
    const {goto} = useRoutingUtils();

    useEffect(() => {
        if(!isEmailValid(email)) {
            setUsePassword(false);
            setPassword("");
            return;
        }

        const to = setTimeout(() => {
            getAuthConfig({
                variables: {
                    email: email
                }
            });
        }, 500);
        return () => clearTimeout(to);
    }, [email]);

    useEffect(() => {
        if(authConfig?.getAuthConfig && !authConfigLoading) {
            const config = authConfig.getAuthConfig;
            if(config.loginType === ClientAuthenticationType.UsernamePassword) {
                setUsePassword(true);
            } else if(config.loginType === ClientAuthenticationType.SingleSignOn) {
                if(config.ssoLoginUrl) {
                    redirectBrowser(config.ssoLoginUrl);
                } else {
                    setError("Unable to determine SSO provider.");
                }
            } else {
                setError("Unable to determine login method.");
            }
        }
    }, [authConfig, authConfigLoading]);

    const signInClicked = async (email: string, password: string): Promise<void> => {
        if(!usePassword) {
            return;
        }

        setChecking(true);
        try {
            await authContext.signInWithEmail(email, password);
            goto("/");
        } catch (err) {
            if ((err as unknown as { code: string }).code === 'UserNotConfirmedException') {
                // history.push('verify')
            } else if((err as unknown as { code: string }).code === "NEW_PASSWORD_REQUIRED") {
                setShowChangePassword(true);
                // We must return here so the password doesn't get cleared.
                return;
            } else {
                setError((err as unknown as { message: string }).message);
            }
        } finally {
            setChecking(false);
        }
    };

    if(showChangePassword) {
        return <SignInChangePassword email={email} oldPassword={password} />;
    }

    return <ThemeProvider theme={ViziblyTheme}>
        <div className={css.signInPage}>
            <Grid>
            <Row className={css.row}>
                <Col>
                    <form onSubmit={(evt) =>  {evt.preventDefault(); signInClicked(email, password);}} autoComplete="off">
                        <div className={css.loginForm}>
                            <div>
                                <div className={css.welcomeBack}>Welcome back to</div>
                                <img src={logo} alt="vizibly-logo" />
                                <div className={css.loginAccess}>Log in to access your account.</div>
                            </div>
                            {error &&
                                    <p>
                                        The username and password you entered did not match our records. Please try again.
                                    </p>
                            }
                            <Field className={css.loginInput}>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={email}

                                    onChange={(value) => setEmail(value.target.value)}
                                    required
                                />
                            </Field>

                            {usePassword ?
                                <>
                                    <Field className={css.loginInput}>
                                        <Label>Password</Label>
                                        {!showPassword ?
                                            <>
                                                <Input
                                                    type="password"
                                                    value={password}
                                                    onChange={(value) => setPassword(value.target.value)}
                                                    required
                                                />
                                                <Button className={css.showHidePassword} isBasic onClick={() => setShowPassword(true)}><Eye height="26px" width="26px" aria-label="View Password" /></Button>
                                            </>:
                                            <>
                                                <Input
                                                    type="text"
                                                    value={password}
                                                    onChange={(value) => setPassword(value.target.value)}
                                                    required
                                                />
                                                <Button className={css.showHidePassword} isBasic onClick={() => setShowPassword(false)}>
                                                    {showPassword ?
                                                        <VisibilityOff /> :
                                                        <Visibility />
                                                    }
                                                </Button>
                                            </>
                                        }
                                    </Field>
                                    <div className={css.formButtons}>
                                        <Link className={css.forgotLink} to="/login/forgot">Forgot your password?</Link>
                                        <Button
                                            type="submit"
                                            isPrimary
                                            onClick={() => {
                                                signInClicked(email, password);
                                            }}
                                            disabled={checking}
                                        >Log In</Button>
                                    </div>
                                </> :
                                <>
                                    <div className={`${css.formButtons} ${css.singleFormButton}`}>
                                        <Button type="button" isPrimary onClick={() => {/* DO NOTHING */}} disabled={!isEmailValid(email) || authConfigLoading || (authConfig && authConfig.getAuthConfig.loginType === ClientAuthenticationType.SingleSignOn)}>Log In</Button>
                                    </div>
                                </>
                            }
                        </div>
                    </form>
                </Col>
                <Col>
                    <div className={css.loginImage}>
                        <img src={loginGraphic} alt="assestsPerformance"/>
                        <div className={css.optimizeText}>Optimize the performance of <br/> your assets.</div>
                    </div>
                </Col>
            </Row>
            </Grid>
        </div>
    </ThemeProvider>;
}